import { adjustAvailabilityByTimeZone, FrequentTimeZones, groupCoachAvailabilitiesByDay } from '@airreading/common';
import {
  dayOfWeekSchema,
  MinimalAvailability,
  SortedDaysOfWeek,
  byDaysOfWeek,
  nextDayOf,
  TeachingPreference,
} from '@airreading/common/data';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

export const coachAvailabilityFormSchema = z.object({
  teachingPreferences: z.array(z.nativeEnum(TeachingPreference)),
  availabilitiesByDay: z.array(
    z.object({
      day: dayOfWeekSchema,
      availabilities: z.array(
        z.object({
          startPlainTime: z.instanceof(Temporal.PlainTime),
          endPlainTime: z.instanceof(Temporal.PlainTime),
        }),
      ),
    }),
  ),
});
export type CoachAvailabilityFormState = z.infer<typeof coachAvailabilityFormSchema>;

export const teachingPreferenceOptions = [TeachingPreference.regular_schedule, TeachingPreference.substitute_teaching];

/**
 * Consolidate availabilities that were split due to timezone adjustments.
 * When availabilities span midnight, they get stored as separate records but should
 * be displayed as a single continuous availability.
 */
function consolidateSplitAvailabilities(availabilities: MinimalAvailability[]): MinimalAvailability[] {
  if (availabilities.length <= 1) return availabilities;

  const sorted = [...availabilities].sort((a, b) => {
    const dayOrder = byDaysOfWeek(a.day, b.day);
    return dayOrder !== 0 ? dayOrder : Temporal.PlainTime.compare(a.startPlainTime, b.startPlainTime);
  });

  const result: MinimalAvailability[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = result[result.length - 1];

    const nextDay = nextDayOf(last.day);

    const timeGapSeconds = current.startPlainTime.since(last.endPlainTime).total('seconds');
    const isSameDayConsecutive = current.day === last.day && timeGapSeconds <= 1;

    const midnight = Temporal.PlainTime.from({ hour: 0 });
    const oneSecondUntilMidnight = Temporal.PlainTime.from({ hour: 23, minute: 59, second: 59 });

    const isMidnightConsecutive =
      current.day === nextDay &&
      Temporal.PlainTime.compare(current.startPlainTime, midnight) <= 1 &&
      Temporal.PlainTime.compare(last.endPlainTime, oneSecondUntilMidnight) >= 0;

    if (isSameDayConsecutive || isMidnightConsecutive) {
      // If the current availability would create an invalid time, we don't merge it with the last availability
      const wouldCreateInvalidTime = Temporal.PlainTime.compare(current.endPlainTime, last.startPlainTime) < 0;

      if (!wouldCreateInvalidTime) {
        result[result.length - 1] = {
          ...last,
          endPlainTime: current.endPlainTime,
        };
        continue;
      }
    }
    result.push(current);
  }
  return result;
}

/**
 * Convert a list of availabilities from US Central time to the coach's time zone and group them by day.
 * @param availabilities DB stored availabilities in US Central time
 * @param coachTimeZone Coach's time zone to convert the availabilities to
 */
export function groupAvailabilitiesByDay(
  availabilities: Array<MinimalAvailability>,
  coachTimeZone: string,
): CoachAvailabilityFormState {
  const convertedToCoachTimeZone = availabilities.map((availability) => {
    const startPlainTime =
      availability.startPlainTime instanceof Temporal.PlainTime
        ? availability.startPlainTime
        : Temporal.PlainTime.from(availability.startPlainTime);
    const endPlainTime =
      availability.endPlainTime instanceof Temporal.PlainTime
        ? availability.endPlainTime
        : Temporal.PlainTime.from(availability.endPlainTime);

    const {
      startAdjustedPlainTime,
      endAdjustedPlainTime,
      adjustedDay: adjustedDayInCoachTimeZone,
    } = adjustAvailabilityByTimeZone(
      { ...availability, startPlainTime, endPlainTime },
      {
        sourceTimeZone: FrequentTimeZones.UsCentral,
        targetTimeZone: coachTimeZone,
      },
    );

    return {
      day: adjustedDayInCoachTimeZone,
      startPlainTime: startAdjustedPlainTime,
      endPlainTime: endAdjustedPlainTime,
    };
  });
  const consolidated = consolidateSplitAvailabilities(convertedToCoachTimeZone);
  const groupedByDay = groupCoachAvailabilitiesByDay(consolidated);

  return {
    availabilitiesByDay: SortedDaysOfWeek.map((day) => ({
      day,
      availabilities: groupedByDay.get(day) ?? [],
    })),
    teachingPreferences: [TeachingPreference.regular_schedule],
  };
}
